import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, PencilIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { ConciergePartnerForm } from '../../components/forms/ConciergePartnerForm'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import type { ConciergePartner, ConciergePartnerInput } from '../../../shared/types'

const partnersApi = createCrudApi<ConciergePartner, ConciergePartnerInput>('concierge-partners')

// Category badge component
function CategoryBadge({ category }: { category: string }) {
  const colors = {
    hospitality: 'bg-amber-100 text-amber-800',
    music: 'bg-pink-100 text-pink-800',
    wine: 'bg-red-100 text-red-800',
    sport: 'bg-green-100 text-green-800',
    maritime: 'bg-cyan-100 text-cyan-800',
    auto: 'bg-orange-100 text-orange-800',
  }
  return (
    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
      {category}
    </span>
  )
}

export function ConciergePartnersPage() {
  const [showForm, setShowForm] = useState(false)
  const [editingPartner, setEditingPartner] = useState<ConciergePartner | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: response, isLoading } = useQuery({
    queryKey: ['concierge-partners', { page, limit }],
    queryFn: () => partnersApi.list({ page, limit }),
  })

  const partners = response?.data

  const createMutation = useMutation({
    mutationFn: partnersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-partners'] })
      setShowForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<ConciergePartnerInput> }) => partnersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-partners'] })
      setEditingPartner(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: partnersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-partners'] })
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div className="mb-6">
            <Link
              to="/concierge"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.concierge.backToEvents')}
            </Link>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.concierge.partners.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.concierge.partners.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => { setShowForm(!showForm); setEditingPartner(null) }}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.concierge.partners.addButton')}
          </button>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.concierge.partners.newTitle')}</h3>
              <ConciergePartnerForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.concierge.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.concierge.partners.totalPartners')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to="/concierge"
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {t('pages.concierge.backToEvents')}
              </Link>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.concierge.partners.title')}</h1>
            <button
              type="button"
              onClick={() => { setShowForm(!showForm); setEditingPartner(null) }}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {showForm ? t('common.cancel') : t('pages.concierge.partners.addButton')}
            </button>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.concierge.partners.newTitle')}</h2>
                <ConciergePartnerForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t('fields.name')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.category')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('concierge.fields.logoUrl')}
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {partners?.map((partner) => (
                        <tr key={partner.id} className="hover:bg-gray-50 transition-colors">
                          {editingPartner?.id === partner.id ? (
                            <td colSpan={4} className="p-4">
                              <ConciergePartnerForm
                                defaultValues={nullToUndefined(partner)}
                                onSubmit={(data) => updateMutation.mutate({ id: partner.id, data })}
                                onCancel={() => setEditingPartner(null)}
                                loading={updateMutation.isPending}
                              />
                            </td>
                          ) : (
                            <>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                <div className="flex items-center gap-2">
                                  {partner.logoUrl && (
                                    <img src={partner.logoUrl} alt={partner.name} className="h-8 w-auto" />
                                  )}
                                  {partner.name}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <CategoryBadge category={partner.category} />
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {partner.logoUrl || '-'}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  onClick={() => { setEditingPartner(partner); setShowForm(false) }}
                                  className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-4"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                  {t('common.edit')}
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm(t('pages.concierge.partners.confirmDelete'))) {
                                      deleteMutation.mutate(partner.id)
                                    }
                                  }}
                                  className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  {t('common.delete')}
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {response && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
